import * as React from 'react';

import { type Specialization_Specialization as S12n } from '__generated__/graphql-types-do-not-use';

import type { TrackingData } from 'js/lib/retracked';

import { getProductDifficultyLevelsTranslated } from 'bundles/browse/utils';
import { PATHWAYS_COLLECTION_CAROUSEL_PRODUCT_CARD_IMAGE_DEFAULT_VALUES } from 'bundles/collections-carousel/constants';
import type { S12nTargetDegreesFragment as TargetDegrees } from 'bundles/page-config-common/queries/__generated__/GetS12nTargetDegreesQuery';
import { isPathwaysForAllEnabled } from 'bundles/page-config-common/utils/isPathwaysForAllEnabled';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import { getImagePropOverrides } from 'bundles/product-card/components/utils/productCardV2Utils';
import GradientBadge from 'bundles/unified-common/components/recommended-pathway/GradientBadge';
import PathwayCourseProgression from 'bundles/unified-common/components/recommended-pathway/PathwayCourseProgression';
import RecommendedPathwayBody from 'bundles/unified-common/components/recommended-pathway/RecommendedPathwayBody';
import RecommendedPathwayButtons from 'bundles/unified-common/components/recommended-pathway/RecommendedPathwayButtons';
import getPathwayUpsellCaption from 'bundles/unified-common/utils/getPathwayUpsellCaption';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';

import _t from 'i18n!nls/consumer-home';

const LAZY_LOAD_THRESHOLD = 4;

type Options = {
  hideCoachButton?: boolean;
};

export const getRecommendedPathwayProps = (
  trackingData: TrackingData,
  item: S12n,
  itemIndex: number,
  pathways: S12n[],
  role: { name: string; slug: string; id?: string },
  collectionIndex: number,
  targetDegrees?: TargetDegrees,
  nameSize?: 'titleSmall' | 'subtitleLarge' | 'subtitleMedium',
  twoColumnLayout?: boolean,
  options?: Options,
  productTypeWithCourseCount?: string
) => {
  const enablePathwaysForAll = isPathwaysForAllEnabled();
  const metadata: string[] = [];
  const translatedDifficultyLevel = getProductDifficultyLevelsTranslated(item.difficultyLevel);

  if (translatedDifficultyLevel) {
    metadata.push(translatedDifficultyLevel);
  }
  if (item.monthsToCompleteOverride) {
    metadata.push(_t('#{months} months', { months: item.monthsToCompleteOverride }));
  }
  if (targetDegrees?.items?.[0]?.stackingType) {
    metadata.push(getPathwayUpsellCaption(targetDegrees.items[0].stackingType));
  }

  const commonProps = {
    url: item.urlPath,
    nameSize,
    body: (
      <RecommendedPathwayBody
        skills={
          item?.skillTags?.filter((skill) => Boolean(skill.skillName)).map((skill) => skill.skillName) as string[]
        }
        averageFiveStarRating={item.rating?.averageFiveStarRating}
        ratingCount={item.rating?.ratingCount}
        metadata={metadata}
      />
    ),
    buttons: [
      <RecommendedPathwayButtons
        key={`pathway-button-${item.id}`}
        item={item}
        itemIndex={itemIndex}
        pathways={pathways}
        role={role}
        trackingData={{
          productCard: {
            index: itemIndex,
            recommender: trackingData.productCard.recommender,
            pageConfigComponent: trackingData.productCard.pageConfigComponent,
          },
          carousel: trackingData.carousel,
        }}
        hideCoachButton={options?.hideCoachButton}
      />,
    ],
    trackingData: {
      productCard: {
        index: itemIndex,
        recommender: trackingData.productCard.recommender,
        pageConfigComponent: trackingData.productCard.pageConfigComponent,
      },
      id: item.id,
      slug: item.slug,
      carousel: {
        ...trackingData.carousel,
        section: collectionIndex,
      },
    },
  };

  if (enablePathwaysForAll) {
    const partners = item.partners.map((partner) => ({
      id: partner.id,
      name: partner.name,
      logoUrl: partner.squareLogo,
    }));
    const imageParams = {
      ...PATHWAYS_COLLECTION_CAROUSEL_PRODUCT_CARD_IMAGE_DEFAULT_VALUES,
      ...getImagePropOverrides(partners, item.cardImageUrl),
    };

    return {
      ...commonProps,
      partners,
      slug: item.slug,
      ImageComponent: (
        <LearningProductCardImage
          imageUrl={item.cardImageUrl}
          enableFluidWidthForLazyImg
          enableLazyLoading={itemIndex > LAZY_LOAD_THRESHOLD}
          imageParams={{
            ...imageParams,
          }}
        />
      ),
      linkProps: { href: item.urlPath, component: TrackedLink2 },
      avgProductRating: item.rating?.averageFiveStarRating,
      numProductRatings: item.rating?.ratingCount,
      statusTags:
        itemIndex === 0
          ? { tags: [_t('Top recommendation')], tagNames: ['Top recommendation'] }
          : { tags: [], tagNames: [] },
      customMetadata: [
        translatedDifficultyLevel,
        productTypeWithCourseCount,
        item.monthsToCompleteOverride ? _t('#{months} months', { months: item.monthsToCompleteOverride }) : undefined,
      ].filter(Boolean),
      skills: item.skillTags?.map((s) => s.skillName) ?? [],
    };
  }

  return {
    ...commonProps,
    name: getProductTitle(item.name, item.productVariant, item.slug),
    badge: itemIndex === 0 ? <GradientBadge label={_t('Top recommendation')} /> : undefined,
    logo: item.partners[0]?.squareLogo,
    courseProgression: (
      <PathwayCourseProgression
        courseData={item.courses}
        totalCourses={item.courses.length}
        twoColumnLayout={twoColumnLayout}
      />
    ),
  };
};
